/**
 * The Mewtwo Mystery — a slow, optional 5-beat story thread woven between the eight
 * gym badges (D17; notes/story-design/main-quest.md). It ties into the existing
 * authored codex (`mewtwo-origin` "The One That Was Made", `mew-origin`) and the
 * roaming-legendary overlay (`core/legendary.ts`, which already drifts Mewtwo toward
 * `old-ruins`).
 *
 * The triple contract holds absolutely: nothing here is timed or missable, no beat
 * ever gates a badge, and the finale is *ambient wonder you can ignore entirely* — a
 * fled legendary always comes back around. Team Meridian, the "antagonists," are warm
 * repentant researchers, never menacing to the player.
 *
 * Delivery: each beat is a `LoreBeat` the story-director selects + the
 * `TemplateStoryVoice` phrases from the authored floor (verbatim `data.line`), or the
 * `LLMVoice` phrases richer when on. Structured `data` grounds the voice; the authored
 * seed lines below are the floor. Beats unlock by BADGE COUNT expressed as data
 * (`requiredBadges`) so the session/story-director can evaluate it — never a hard gate.
 */
import type { CodexEntry, LoreBeat } from '@/shared';

/** The thread id every Mewtwo beat + codex growth shares. */
export const MEWTWO_THREAD_ID = 'mewtwo-thread';

/**
 * The authored seed lines, verbatim from main-quest.md, kept as the full set per beat.
 * The floor renders the lead line (`data.line`); the LLMVoice may draw on all of them
 * for a richer multi-voice exchange. Team Meridian's lines (beats C/D) are warm and
 * a little too eager — never menacing.
 */
export const MEWTWO_BEAT_SEEDS: Readonly<Record<string, readonly string[]>> = {
  'mewtwo-beat-a': [
    "Funny thing on the ridge today — dead quiet, not a leaf moving, and I'd swear something was watching.",
    "Old folks say that hush has a name, but they won't say it indoors.",
    'Probably nothing. Probably.',
  ],
  'mewtwo-beat-b': [
    'Marlow saw it — up over the crags, pale against the storm, gone before he could breathe.',
    "He's not one to make things up, that one.",
    "Whatever it is, it's real. And it's near.",
  ],
  'mewtwo-beat-c': [
    'Some folks in matching coats came through, asking after the hush. Polite as anything — a bit too eager, maybe.',
    'They call themselves Meridian. Say they only want to understand it.',
    "Understanding's fine. It's the finding I'd go gentle about.",
  ],
  'mewtwo-beat-d': [
    'Beneath the murals, Enigma says, is a room the ruins grew over on purpose — pale tile, dead lamps, and a tank long empty.',
    'The town never speaks of it. Meridian goes quiet standing there, the way you go quiet at a grave you are partly responsible for.',
    "They aren't its villains, Enigma reckons — only its guilty grandchildren, come a long way to say sorry.",
  ],
  'mewtwo-beat-e': [
    'Cinder lit the eighth lantern tonight, and the hush came down off the mountain to see.',
    "Meridian didn't bring a single ball. Just sat on the shrine step, like the rest of us.",
    "It watched you a long while, they say. The town rather hopes it's found its reason to stay.",
  ],
};

/**
 * A single Mewtwo beat's authored shape. `requiredBadges` is the data-expressed unlock
 * the session/story-director evaluates against the player's badge count (1, 3, 5, 7, 8)
 * — a soft cadence, never a wall: race to badge 8 and you simply get them in a rush.
 */
interface MewtwoBeatSpec {
  readonly id: string;
  readonly kind: string; // 'roaming-rumor' | 'town-legend' | 'codex-unlock'
  readonly requiredBadges: number;
  readonly subject: string; // FLOOR_GRAMMAR fallback if no line is carried
  /** Codex entry this beat opens, if any (beat D). */
  readonly codexEntryId?: string;
}

const MEWTWO_BEAT_SPECS: readonly MewtwoBeatSpec[] = [
  {
    id: 'mewtwo-beat-a',
    kind: 'roaming-rumor',
    requiredBadges: 1,
    subject: 'a hush on the routes',
  },
  {
    id: 'mewtwo-beat-b',
    kind: 'town-legend',
    requiredBadges: 3,
    subject: 'the sighting over the crags',
  },
  {
    id: 'mewtwo-beat-c',
    kind: 'town-legend',
    requiredBadges: 5,
    subject: 'the folks in matching coats',
  },
  {
    id: 'mewtwo-beat-d',
    kind: 'codex-unlock',
    requiredBadges: 7,
    subject: 'the hollow lab beneath the ruins',
    codexEntryId: 'the-hollow-lab',
  },
  {
    id: 'mewtwo-beat-e',
    kind: 'roaming-rumor',
    requiredBadges: 8,
    subject: 'the one that watched',
  },
];

/** Build one Mewtwo LoreBeat from its spec. Pure: a function of (spec, at). */
function buildBeat(spec: MewtwoBeatSpec, at: number): LoreBeat {
  const data: Record<string, string | number | boolean> = {
    subject: spec.subject,
    line: MEWTWO_BEAT_SEEDS[spec.id]![0]!,
    requiredBadges: spec.requiredBadges,
    warm: true,
  };
  if (spec.codexEntryId) data.codexEntryId = spec.codexEntryId;
  return {
    id: spec.id,
    kind: spec.kind,
    threadId: MEWTWO_THREAD_ID,
    data,
    createdAt: at,
  };
}

/**
 * The Mewtwo beats unlocked at a given badge count, freshest cadence preserved by the
 * caller (`selectLoreBeats` sorts). `at` stamps them (caller-supplied → deterministic).
 * Never throws; an empty result (0 badges) just means the thread hasn't stirred yet.
 */
export function mewtwoThreadBeats(badgeCount: number, at = 0): readonly LoreBeat[] {
  return MEWTWO_BEAT_SPECS.filter((s) => badgeCount >= s.requiredBadges).map((s) =>
    buildBeat(s, at),
  );
}

/** All five Mewtwo beats regardless of badge count (for tests + LLM grounding). */
export function allMewtwoBeats(at = 0): readonly LoreBeat[] {
  return MEWTWO_BEAT_SPECS.map((s) => buildBeat(s, at));
}

// ── The three NEW codex entries the thread unlocks (via quest reward codex-unlock) ──
//
// These are NOT ambient seeds — they are granted by their quests' `codex-unlock`
// reward (Agent B's grantReward pushes them into LoreState.entries). Their `unlock`
// carries the badge-count intent as data; `codex.isUnlocked` fails closed on the
// 'badges' kind, so they never leak into the codex before the quest grants them.

export const MEWTWO_CODEX_ENTRIES: readonly CodexEntry[] = [
  {
    id: 'the-hollow-lab',
    title: 'The Hollow Lab',
    kind: 'legend',
    body:
      'Enigma, who reads the Unown script no one else will, will tell you — quietly, and only if you ' +
      'have earned the telling — that beneath the murals of the Sunken Ruins is a room the ruins grew ' +
      'over on purpose. Pale tile. Dead lamps in long rows. A great glass tank, drained and silent, its ' +
      'seams gone soft with dust. The town does not speak of it. It is not that anyone forbids it; it is ' +
      'only that no story anybody wants to tell begins there.\n\n' +
      'This, the old pages agree, is where the one that was made first opened its eyes — where it ' +
      'understood in a single cold instant what it was and why, and found that neither answer was the ' +
      'same as being loved. The learned people who bent over their lamps here got exactly the power they ' +
      'set out to make, and were not ready for a moment of it. When it left, the leaving was not gentle, ' +
      'and the lamps have been dark ever since.\n\n' +
      'The travelers in matching coats — Team Meridian — go very quiet when they stand in that room. ' +
      'They are not its villains, whatever the coats might suggest. They are its inheritors: the ' +
      'grandchildren of the hands that built the tank, come a long and careful way to apologize to ' +
      'something that may no longer be listening. You go quiet too, the way you go quiet at a grave you ' +
      'are partly responsible for, even when the fault was never yours to carry.\n\n' +
      'Enigma leaves a light burning on the top step now, the way you would for someone you hoped might, ' +
      'one evening, decide to come home. It costs nothing, she says, to keep a light on. And the ruins, ' +
      'for all they have grown over that pale cold room, have never once put it out.',
    unlock: { kind: 'badges', threshold: 7 },
    relatedSpeciesIds: ['mewtwo', 'mew'],
  },
  {
    id: 'the-reason',
    title: 'The Reason It Was Looking For',
    kind: 'legend',
    body:
      'On the night the eighth lantern was lit, the hush came down off the mountain to see. That is how ' +
      'Cinder tells it, and Cinder is not given to embroidery. The town had gathered at the little shrine ' +
      'nobody built, the way it gathers for the things that matter, and the pale watcher of the high quiet ' +
      'places came nearer than it had come in living memory — and simply watched.\n\n' +
      'Team Meridian were there, and this is the part the town will retell for years: they did not bring a ' +
      'single ball. All that listening equipment, all those careful instruments, put away. They sat on the ' +
      'worn shrine step among the rest, hands empty, and left a berry on the ledge like anyone else, and ' +
      'waited to see what a thing so long alone might choose when nobody was reaching for it.\n\n' +
      'What had it been looking for, all those quiet years on the ridges? Not a battle; it could have ' +
      'flattened the mountain if it wished. Not a captor; it had already refused that, and paid for the ' +
      'refusing. It had been looking, the breeders now think, for a reason to have been made at all that it ' +
      'could choose for itself — and it had spent a long while weighing, from very far away, whether the ' +
      'small kindnesses people showed one another might extend to it.\n\n' +
      'It watched you a long while, they say — you, who had walked the whole circuit gently, who had never ' +
      'once raised a hand to a friend. Whether it stays is its own choice, now and always; a thing like ' +
      'that is never caught, only welcomed, and welcome is a door left open, not a lock. But the town has ' +
      'rather begun to hope. The lanterns are lit. The step is swept. The berry is on the ledge. And the ' +
      'hush, for the first time in the telling of it, does not seem to be in any hurry to leave.',
    unlock: { kind: 'badges', threshold: 8 },
    relatedSpeciesIds: ['mewtwo'],
  },
  {
    id: 'the-circuit-walked',
    title: 'The Circuit, Walked',
    kind: 'tale',
    body:
      'Every chronicle the keepers tend has a page like this one, and yours has just been written: the ' +
      'page for the trainer who walked the whole circuit, badge by badge, from the sunny meadow to the ' +
      'ashland shrine — and was cheered at every gym, win or lose, because out here the measure was never ' +
      'the winning.\n\n' +
      'Rowan measured how well you and your team listen to one another. Tansy heard your whole team say ' +
      'hello. Marlow watched you be patient; Bruno left a light on in the deep; Sage saw the good kind of ' +
      'showing, the growth that comes of years of care. Kestrel gave you the view. Enigma trusted you with ' +
      'the story under the stones. And Cinder — warmest of the eight — lit the last lantern and said you ' +
      'had not come all that way to be nervous now.\n\n' +
      'Eight leaders, eight biomes, eight small proofs that a team is built and not handed over, that the ' +
      'strength that matters is the kind your friends are glad to have. No one hurried you. Nothing you ' +
      'missed is gone; a fled encounter always comes back around. You simply walked, and kept walking, and ' +
      'the region walked a while beside you.\n\n' +
      'The town lights eight lanterns in your honor now — not a trophy, not a finish line, just a warm ' +
      'little constellation at the edge of the square that says, to anyone wandering in after a long day: ' +
      'someone came all this way here, gently, and was glad of the company. The page stays open. There is, ' +
      'as ever, all the time in the world for whatever you feel like doing next.',
    unlock: { kind: 'badges', threshold: 8 },
    relatedSpeciesIds: [],
  },
];

/** Look up one of the thread's codex entries by id (for reward grants). */
export function mewtwoCodexEntry(id: string): CodexEntry | undefined {
  return MEWTWO_CODEX_ENTRIES.find((e) => e.id === id);
}
